// janus-engine/spells/prepared-spells.ts — Lane L7 (2026-09-26): the PURE prepared-spell rules the browser decides before a
// commit (options/game/spells/prepare_spells/spell_prep_truth_commit_inc.php). Refusals carry PHP's exact text
// (prepare_spell_inc.php / update_prepared_spells_list_inc.php — owner: the front-end gate is the gate).
//   - preparedFingerprint: the CAS string, byte-identical to spell_prep_truth_lib_inc.php ft_spell_prep_fingerprint().
//   - resolvePrepare: the feat gate, then the cantrip / spell COUNT caps (build_known_spells_numbered_inc.php semantics, `>=`).
//   - resolveSwitch: the in-combat rule (a `prepare_spells_in_combat` passive — Quick Study — exempts), then the arcane-threshold rule (PHP's `>=` against a possibly
//     null threshold: a null threshold compares as bool, so ANY held Arcane Fatigue refuses).
//   - applyPrepAction: the prepared map after a write (the same four writes ft_spell_prep_persist() performs).
//   - planActiveKnownIds: the §2D2 spells_known identity plan for the ACTIVE list, through the frozen compile module.
import { mergePreparedSpellIdentities } from '../compile/prepared-spell-identity-merge';

export const SPELL_PREP_SCHEMA = 'janus.spell_prep.v1';
export const ARCANE_FATIGUE_ID = 'CEF623E6-6A71-37FD-29C7-12988B43151A';
export type SpellPrepAction = 'prepare' | 'unprepare' | 'switch' | 'reset';
/** class_id => spell_list => [spell_id …] — the blob's `encounter_creatures_spells_prepared` (elided when empty). */
export type PreparedMap = Record<string, Record<string, string[]>>;

export const PREP_REFUSALS = {
    feat: 'This spell requires a feat you do not have.',
    cantrips: 'Trying to prepare too many cantrips!',
    spells: (prepared: number, max: number): string => `Trying to prepare too many spells! ${prepared} of ${max}`,
    combat: 'Preparing a new spell requires intense concentration. You cannot prepare new spells while in combat!',
    fatigue: 'You have already prepared more spells than your Essence or Arcane Threshold can handle. You must Long Rest to reset your Arcane Fatigue.',
    list: 'Unknown spell list.',
} as const;

const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** Tolerant reader for a stored prepared map (JSON objects; a PHP list may arrive as an array). */
export function normalizePreparedMap(raw: unknown): PreparedMap {
    const out: PreparedMap = {};
    if (raw === null || typeof raw !== 'object') return out;
    for (const [cid, lists] of Object.entries(raw as Record<string, unknown>)) {
        if (lists === null || typeof lists !== 'object') continue;
        const entries: [string, unknown][] = Array.isArray(lists) ? lists.map((v, i) => [String(i), v]) : Object.entries(lists as Record<string, unknown>);
        for (const [sl, ids] of entries) {
            if (!Array.isArray(ids)) continue;
            const clean = ids.map(String).filter(Boolean);
            if (clean.length) ((out[cid] ??= {})[sl] = clean);
        }
    }
    return out;
}

/** Sorted unique "CLASS|LIST|SPELL" triples + "#<active>" (PHP sort SORT_STRING = byte order; ids are ASCII). */
export function preparedFingerprint(map: PreparedMap, active: number): string {
    const t = new Set<string>();
    for (const [cid, lists] of Object.entries(map)) for (const [sl, ids] of Object.entries(lists)) for (const sid of ids) t.add(`${upper(cid)}|${Math.trunc(Number(sl))}|${upper(sid)}`);
    const keys = [...t].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    return `${keys.join(',')}#${Math.trunc(active)}`;
}

export function preparedIds(map: PreparedMap, classId: string, list: number): string[] {
    for (const [cid, lists] of Object.entries(map)) if (upper(cid) === upper(classId)) return [...(lists[String(list)] ?? [])];
    return [];
}
export function isPrepared(map: PreparedMap, classId: string, list: number, spellId: string): boolean {
    return preparedIds(map, classId, list).some(id => upper(id) === upper(spellId));
}

export interface PrepareGateInput {
    level: number;
    /** caster_has_spell_requires_feat — the prepare pool already drops a spell whose feat the caster lacks. */
    featOk: boolean;
    cantripsPrepared: number; maxCantrips: number;
    spellsPrepared: number; maxSpells: number;
}
export type Verdict = { ok: true } | { ok: false; message: string };

/** prepare_spell_inc.php's gates in their order: feat, then the tier's count cap (`>=`: an over-prepared list stays refused). */
export function resolvePrepare(i: PrepareGateInput): Verdict {
    if (!i.featOk) return { ok: false, message: PREP_REFUSALS.feat };
    if (i.level === 0 && i.cantripsPrepared >= i.maxCantrips) return { ok: false, message: PREP_REFUSALS.cantrips };
    if (i.level > 0 && i.spellsPrepared >= i.maxSpells) return { ok: false, message: PREP_REFUSALS.spells(i.spellsPrepared, i.maxSpells) };
    return { ok: true };
}

export interface SwitchGateInput {
    inCombat: boolean;
    /** holdsActiveInCombatSpellPrep(): an active passive carries `prepare_spells_in_combat`. */
    canPrepareInCombat: boolean;
    /** get_caster_status_effect_group_stack(caster, Arcane Fatigue): null when the caster holds none. */
    fatigueStacks: number | null;
    /** layer_value(caster, 'arcane_threshold') — null when absent. */
    arcaneThreshold: number | null;
}
/** update_prepared_spells_list_inc.php's gates in their order: combat (unless `prepare_spells_in_combat`), then the arcane threshold. */
export function resolveSwitch(i: SwitchGateInput): Verdict {
    if (!i.canPrepareInCombat && i.inCombat) return { ok: false, message: PREP_REFUSALS.combat };
    if (i.fatigueStacks !== null && (i.arcaneThreshold === null || i.fatigueStacks >= i.arcaneThreshold)) return { ok: false, message: PREP_REFUSALS.fatigue };
    return { ok: true };
}

export interface PrepActionInput { action: SpellPrepAction; classId?: string; spellList: number; spellId?: string }
/** The prepared map after ft_spell_prep_persist() (prepare appends at the end; unprepare drops every match; reset empties
 * the list for EVERY class; switch leaves the map alone). Returns a fresh map; empty lists / classes are elided. */
export function applyPrepAction(map: PreparedMap, a: PrepActionInput): PreparedMap {
    const out: PreparedMap = {};
    for (const [cid, lists] of Object.entries(map)) for (const [sl, ids] of Object.entries(lists)) (out[cid] ??= {})[sl] = [...ids];
    const list = String(a.spellList);
    if (a.action === 'prepare' && a.classId && a.spellId) {
        const cid = Object.keys(out).find(k => upper(k) === upper(a.classId)) ?? upper(a.classId);
        const ids = ((out[cid] ??= {})[list] ??= []);
        if (!ids.some(id => upper(id) === upper(a.spellId))) ids.push(upper(a.spellId));
    } else if (a.action === 'unprepare' && a.classId && a.spellId) {
        for (const [cid, lists] of Object.entries(out)) { const ids = lists[list]; if (upper(cid) === upper(a.classId) && ids) lists[list] = ids.filter(id => upper(id) !== upper(a.spellId)); }
    } else if (a.action === 'reset') {
        for (const lists of Object.values(out)) delete lists[list];
    }
    for (const [cid, lists] of Object.entries(out)) {
        for (const [sl, ids] of Object.entries(lists)) if (ids.length === 0) delete lists[sl];
        if (Object.keys(lists).length === 0) delete out[cid];
    }
    return out;
}

/** Cantrip / spell counts of (class, list) given each id's level (an id with no known level counts as a spell). */
export function countPrepared(map: PreparedMap, classId: string, list: number, levelOf: (id: string) => number | undefined): { cantrips: number; spells: number } {
    let cantrips = 0, spells = 0;
    for (const id of preparedIds(map, classId, list)) { if ((levelOf(upper(id)) ?? 1) === 0) cantrips++; else spells++; }
    return { cantrips, spells };
}

/** The §2D2 merge's identity plan for the ACTIVE list: innate ids first, then every active prepared id (all classes, in
 * order) not already present — the frozen compile module's rule (finalize_creature_abilities.php §2D2). */
export function planActiveKnownIds(innateIds: readonly string[], map: PreparedMap, active: number): string[] {
    const candidates: { spellId: string; resolved: boolean }[] = [];
    for (const lists of Object.values(map)) for (const id of lists[String(active)] ?? []) candidates.push({ spellId: upper(id), resolved: true });
    return mergePreparedSpellIdentities({ existingIds: innateIds.map(upper), candidates });
}

type AnyRow = Record<string, unknown>;
const rec = (v: unknown): AnyRow | null => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as AnyRow) : null);
const scalarOf = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);

/** get_caster_status_effect_group_stack(caster, Arcane Fatigue) over the row's TARGET status entries: the highest
 * status_stack (lean-elided = 1) of an entry whose status item is Arcane Fatigue; null when none is held. */
export function arcaneFatigueStacks(targetStatuses: readonly unknown[]): number | null {
    let best: number | null = null;
    for (const s of targetStatuses) {
        const row = rec(s);
        if (!row) continue;
        const def = rec(row.status_arr);
        const id = upper(scalarOf(rec(def?.items)?.item_id) ?? scalarOf(row.status_item_id) ?? scalarOf(row.item_id) ?? (typeof row.status_arr === 'string' ? row.status_arr : ''));
        if (id !== ARCANE_FATIGUE_ID) continue;
        const stack = Math.max(1, Math.trunc(Number(scalarOf(row.status_stack) ?? 1)) || 1);
        best = best === null ? stack : Math.max(best, stack);
    }
    return best;
}

const truthy = (f: unknown): boolean => !(f === undefined || f === null || f === false || f === 0 || f === '' || f === '0');

/** spell_prep_truth_commit_inc.php's exemption: passive_ability_active_ability_ids(layer_entries(caster, 'passive_ability'))
 * holds a passive whose definition carries `prepare_spells_in_combat` (Quick Study). An ARRAY entry counts when its stamped
 * passive_conditions_active (when present) is truthy; its definition is the entry itself when it is a full row, else
 * `definitionOf(id)` (the caster row's catalog). A definition the page does not hold cannot exempt. */
export function holdsActiveInCombatSpellPrep(passiveEntries: readonly unknown[], definitionOf: (id: string) => unknown): boolean {
    for (const e of passiveEntries) {
        const row = rec(e);
        if (!row) continue;
        const id = upper(scalarOf(row.ability_id) ?? scalarOf(rec(row.items)?.item_id) ?? scalarOf(row.item_id));
        if (id === '') continue;
        if ('passive_conditions_active' in row && !truthy(scalarOf(row.passive_conditions_active))) continue;
        const def = rec(row.item_layer_types) ? row : rec(definitionOf(id));
        if (truthy(scalarOf(rec(def?.item_layer_types)?.prepare_spells_in_combat))) return true;
    }
    return false;
}
